/**
 * Distribution groups and notification routing.
 *
 * Every email and notification the application sends, in one catalog, and
 * which of them an administrator (or a producer, for one event) may point at a
 * **distribution group** — a named list of people — instead of the audience the
 * code would otherwise choose.
 *
 * Two levels, as in merkos-managment's notification groups:
 *
 * - **Global** (`scope` null): the office's groups and rules. A global rule
 *   applies to the notification wherever it is raised.
 * - **Event** (`scope` `event:<id>`): one event's own groups and rules, whose
 *   members may include that event's own clients (`clientMembers`). A rule
 *   set on an event replaces the global one for that event only; with none, the
 *   global rule applies; with neither, the notification reaches whoever it
 *   reached before groups existed (`defaultAudience`). So nothing goes quiet
 *   the day this ships.
 *
 * The groups and rules themselves are `@speles7172/message-client`'s routing
 * store (`recipient_groups`, `event_routes`); this file is what the application
 * adds to it — which notifications honour a rule, and at which level.
 */
const BOTH = ['in_app', 'email'];
const EMAIL = ['email'];
export const NOTIFICATION_CATALOG = [
    // ── Global (the office as a whole) ─────────────────────────────────────────
    {
        key: 'inquiry.received',
        label: 'New enquiry',
        trigger: 'Someone fills in the enquiry form on the public site',
        category: 'Enquiries',
        level: 'global',
        audience: 'office',
        defaultAudience: 'The producer vendor’s office contacts (App Settings), else every administrator and producer — put on the enquiry’s conversation, plus the office notice email. A routed group takes the conversation; the rule’s email switch governs the notice email only',
        routable: true,
        channels: EMAIL,
    },
    {
        key: 'task.office_summary',
        label: 'Office task summary',
        trigger: 'Each morning, when office tasks are overdue or have nobody assigned',
        category: 'Tasks',
        level: 'global',
        audience: 'office',
        defaultAudience: 'Every administrator',
        routable: true,
        channels: EMAIL,
    },
    // ── Event (about one event) — routable ─────────────────────────────────────
    {
        key: 'client.payment.received',
        label: 'Client payment received',
        trigger: 'A client payment is recorded on an event',
        category: 'Money',
        level: 'event',
        audience: 'office',
        defaultAudience: 'Every administrator and producer, except whoever recorded it',
        routable: true,
        channels: BOTH,
        clientMembers: 'fullBudget',
    },
    {
        key: 'expense.payment-due',
        label: 'Vendor payment due',
        trigger: 'An expense with a balance left is coming due (the morning run)',
        category: 'Money',
        level: 'event',
        audience: 'office',
        defaultAudience: 'Every administrator and producer',
        routable: true,
        channels: BOTH,
    },
    {
        key: 'expense.approved',
        label: 'Client approved an expense',
        trigger: 'A client approves an expense in the portal',
        category: 'Money',
        level: 'event',
        audience: 'office',
        defaultAudience: 'Every administrator and producer',
        routable: true,
        channels: BOTH,
        clientMembers: 'fullBudget',
    },
    {
        key: 'expense.rejected',
        label: 'Client rejected an expense',
        trigger: 'A client rejects an expense in the portal',
        category: 'Money',
        level: 'event',
        audience: 'office',
        defaultAudience: 'Every administrator and producer',
        routable: true,
        channels: BOTH,
        clientMembers: 'fullBudget',
    },
    {
        key: 'questionnaire.submitted',
        label: 'Questionnaire submitted',
        trigger: 'A client submits a questionnaire',
        category: 'Clients',
        level: 'event',
        audience: 'office',
        defaultAudience: 'Every administrator and producer',
        routable: true,
        channels: BOTH,
        clientMembers: 'always',
    },
    {
        key: 'guest.client_changes',
        label: 'Client changed the guest list',
        trigger: 'At most hourly, when a client changed guests or seating from the portal',
        category: 'Guests',
        level: 'event',
        audience: 'office',
        defaultAudience: 'Every administrator and producer',
        routable: true,
        channels: BOTH,
        clientMembers: 'always',
    },
    // ── Sent to the person concerned (not routable) ────────────────────────────
    {
        key: 'task.assigned',
        label: 'Task assigned',
        trigger: 'A task is assigned to somebody',
        category: 'Tasks',
        level: 'event',
        audience: 'person',
        defaultAudience: 'The person it was assigned to',
        routable: false,
        channels: BOTH,
    },
    {
        key: 'task.completed',
        label: 'Task completed',
        trigger: 'Somebody else completes a task',
        category: 'Tasks',
        level: 'event',
        audience: 'person',
        defaultAudience: 'Whoever wrote the task',
        routable: false,
        channels: BOTH,
    },
    {
        key: 'task.digest',
        label: 'Daily task email',
        trigger: 'Each morning, for anybody with tasks overdue, due today or coming up',
        category: 'Tasks',
        level: 'global',
        audience: 'person',
        defaultAudience: 'Each person, about their own tasks (Profile → Daily task email)',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'timeline.assigned',
        label: 'Timeline entry assigned',
        trigger: 'Somebody is given a timeline entry',
        category: 'Assignments',
        level: 'event',
        audience: 'person',
        defaultAudience: 'The person it was given to',
        routable: false,
        channels: BOTH,
    },
    {
        key: 'kibuddim.assigned',
        label: 'Kibbud assigned',
        trigger: 'Somebody is given a kibbud',
        category: 'Assignments',
        level: 'event',
        audience: 'person',
        defaultAudience: 'The person it was given to',
        routable: false,
        channels: BOTH,
    },
    {
        key: 'thread.message',
        label: 'New message in a conversation',
        trigger: 'Somebody writes in a conversation',
        category: 'Conversations',
        level: 'event',
        audience: 'person',
        defaultAudience: 'The conversation’s other participants',
        routable: false,
        channels: BOTH,
    },
    {
        key: 'thread.mention',
        label: 'Mentioned in a conversation',
        trigger: 'Somebody is @-mentioned',
        category: 'Conversations',
        level: 'event',
        audience: 'person',
        defaultAudience: 'The person mentioned',
        routable: false,
        channels: BOTH,
    },
    {
        key: 'thread.invited',
        label: 'Added to a conversation',
        trigger: 'Somebody is added to a conversation',
        category: 'Conversations',
        level: 'event',
        audience: 'person',
        defaultAudience: 'The person added',
        routable: false,
        channels: BOTH,
    },
    {
        key: 'thread.status',
        label: 'Conversation status changed',
        trigger: 'A conversation is resolved, closed or reopened',
        category: 'Conversations',
        level: 'event',
        audience: 'person',
        defaultAudience: 'The conversation’s participants',
        routable: false,
        channels: BOTH,
    },
    {
        key: 'upload.received',
        label: 'Files you asked for arrived',
        trigger: 'Somebody sends files through an upload link',
        category: 'Files',
        level: 'event',
        audience: 'person',
        defaultAudience: 'The person who asked for the files',
        routable: false,
        channels: BOTH,
    },
    {
        key: 'upload.request',
        label: 'Files requested',
        trigger: 'Somebody in the office asks for files (Request files)',
        category: 'Files',
        level: 'event',
        audience: 'person',
        defaultAudience: 'The people asked',
        routable: false,
        channels: EMAIL,
    },
    // ── Clients ────────────────────────────────────────────────────────────────
    {
        key: 'payment.receipt',
        label: 'Payment receipt',
        trigger: 'A client payment is recorded (automation, off by default)',
        category: 'Clients',
        level: 'event',
        audience: 'client',
        defaultAudience: 'The event’s clients',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'client.welcome',
        label: 'Client welcome',
        trigger: 'A client is added to an event (automation, off by default)',
        category: 'Clients',
        level: 'event',
        audience: 'client',
        defaultAudience: 'The client added',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'event.status-changed',
        label: 'Event status changed',
        trigger: 'An event’s status changes (automation, off by default)',
        category: 'Clients',
        level: 'event',
        audience: 'client',
        defaultAudience: 'The event’s clients',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'questionnaire.reminder',
        label: 'Questionnaire reminder',
        trigger: 'A questionnaire is still unanswered (automation, off by default)',
        category: 'Clients',
        level: 'event',
        audience: 'client',
        defaultAudience: 'The client it was sent to',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'client.balance-due',
        label: 'Client balance due',
        trigger: 'An event is coming up and its client still owes (automation, off by default)',
        category: 'Clients',
        level: 'event',
        audience: 'client',
        defaultAudience: 'The event’s clients',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'client.thank-you',
        label: 'Thank you after the event',
        trigger: 'An event has ended (automation, off by default)',
        category: 'Clients',
        level: 'event',
        audience: 'client',
        defaultAudience: 'The event’s clients',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'portal.invitation',
        label: 'Portal invitation',
        trigger: 'A producer invites somebody to the client portal',
        category: 'Clients',
        level: 'event',
        audience: 'client',
        defaultAudience: 'The person invited',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'questionnaire.link',
        label: 'Questionnaire link',
        trigger: 'A producer sends a questionnaire',
        category: 'Clients',
        level: 'event',
        audience: 'client',
        defaultAudience: 'The address it is sent to',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'questionnaire.reopened',
        label: 'Questionnaire reopened',
        trigger: 'A producer reopens a submitted questionnaire',
        category: 'Clients',
        level: 'event',
        audience: 'client',
        defaultAudience: 'The address it was sent to',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'user.welcome',
        label: 'Welcome to the app',
        trigger: 'An account is created for somebody',
        category: 'Clients',
        level: 'global',
        audience: 'person',
        defaultAudience: 'The new account',
        routable: false,
        channels: EMAIL,
    },
    // ── Vendors ────────────────────────────────────────────────────────────────
    {
        key: 'vendor.added',
        label: 'Vendor added to an event',
        trigger: 'A vendor is added to an event (automation, off by default)',
        category: 'Vendors',
        level: 'event',
        audience: 'vendor',
        defaultAudience: 'The vendor',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'vendor.quote_reminder',
        label: 'Quote reminder',
        trigger: 'A quote asked for has not come back (automation, off by default)',
        category: 'Vendors',
        level: 'event',
        audience: 'vendor',
        defaultAudience: 'The vendor asked',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'vendor.briefing',
        label: 'Vendor briefing',
        trigger: 'An event is about to happen (automation, off by default)',
        category: 'Vendors',
        level: 'event',
        audience: 'vendor',
        defaultAudience: 'Each vendor on the event',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'vendor.quote_declined',
        label: 'Quote not chosen',
        trigger: 'A quote is accepted and “tell the other vendors” is ticked',
        category: 'Vendors',
        level: 'event',
        audience: 'vendor',
        defaultAudience: 'The vendors not chosen',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'vendor.portal-access',
        label: 'Vendor portal access',
        trigger: 'A vendor contact is given portal access',
        category: 'Vendors',
        level: 'global',
        audience: 'vendor',
        defaultAudience: 'The vendor contact',
        routable: false,
        channels: EMAIL,
    },
    // ── Guests and the public site ─────────────────────────────────────────────
    {
        key: 'guest.invite',
        label: 'Guest invitation',
        trigger: 'An invitation campaign is sent, reminded or retried',
        category: 'Guests',
        level: 'event',
        audience: 'guest',
        defaultAudience: 'Each guest on the campaign',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'guest.rsvp-confirmation',
        label: 'RSVP confirmation',
        trigger: 'A guest answers or changes their RSVP',
        category: 'Guests',
        level: 'event',
        audience: 'guest',
        defaultAudience: 'Every contact of the guest’s party',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'guest.message',
        label: 'Guest broadcast',
        trigger: 'A broadcast is sent to guests',
        category: 'Guests',
        level: 'event',
        audience: 'guest',
        defaultAudience: 'The guests the broadcast is addressed to',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'inquiry.auto-reply',
        label: 'Enquiry auto-reply',
        trigger: 'Someone fills in the enquiry form on the public site',
        category: 'Enquiries',
        level: 'global',
        audience: 'guest',
        defaultAudience: 'The person who wrote in',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'inquiry.office-notice',
        label: 'Enquiry office notice',
        trigger: 'Someone fills in the enquiry form on the public site',
        category: 'Enquiries',
        level: 'global',
        audience: 'office',
        defaultAudience: 'The producer vendor’s address (App Settings), else PRODUCER_EMAIL — or, with a group routed to “New enquiry”, each member',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'vendor.upload.confirmation',
        label: 'Upload receipt',
        trigger: 'Somebody sends files through an upload link',
        category: 'Vendors',
        level: 'event',
        audience: 'vendor',
        defaultAudience: 'Whoever sent the files',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'event.update',
        label: 'Event update',
        trigger: 'An administrator sends it by hand',
        category: 'Clients',
        level: 'event',
        audience: 'client',
        defaultAudience: 'The address it is sent to',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'payment.reminder',
        label: 'Payment reminder',
        trigger: 'An administrator sends it by hand',
        category: 'Clients',
        level: 'event',
        audience: 'client',
        defaultAudience: 'The address it is sent to',
        routable: false,
        channels: EMAIL,
    },
    {
        key: 'quote.received',
        label: 'Quote received',
        trigger: 'An administrator sends it by hand',
        category: 'Clients',
        level: 'event',
        audience: 'client',
        defaultAudience: 'The address it is sent to',
        routable: false,
        channels: EMAIL,
    },
];
/** The notifications a distribution group may be routed to. */
export const ROUTABLE_NOTIFICATIONS = NOTIFICATION_CATALOG.filter((entry) => entry.routable);
/** The routable notifications configurable at a level: an event's own rules cover event notifications only. */
export function routableNotificationsFor(level) {
    return level === 'global'
        ? ROUTABLE_NOTIFICATIONS
        : ROUTABLE_NOTIFICATIONS.filter((entry) => entry.level === 'event');
}
/**
 * Whether a client of the event may hear this notification through an event
 * group, given how the event shows its budget to its clients.
 */
export function clientMayReceive(entry, budgetVisibility) {
    if (!entry?.clientMembers)
        return false;
    return entry.clientMembers === 'always' || budgetVisibility === 'full';
}
/** The routable notifications an event's group may send to its clients. */
export const CLIENT_ROUTABLE_NOTIFICATIONS = NOTIFICATION_CATALOG.filter((entry) => entry.routable && entry.clientMembers).map((entry) => entry.key);
/** The routable entry for a key, or undefined. */
export function routableNotification(key) {
    return ROUTABLE_NOTIFICATIONS.find((entry) => entry.key === key);
}
const EVENT_SCOPE_PREFIX = 'event:';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** The routing scope of an event's own groups and rules; null is global. */
export function notificationScope(eventId) {
    return eventId ? `${EVENT_SCOPE_PREFIX}${eventId}` : null;
}
/**
 * The event a scope names, `null` for the global scope, or `undefined` for a
 * value that is neither — which a caller refuses rather than store a rule under
 * a scope nothing ever raises a notification in.
 */
export function eventIdOfScope(scope) {
    if (scope === null || scope === undefined || scope === '')
        return null;
    if (!scope.startsWith(EVENT_SCOPE_PREFIX))
        return undefined;
    const id = scope.slice(EVENT_SCOPE_PREFIX.length);
    return UUID.test(id) ? id : undefined;
}
//# sourceMappingURL=notification-routing.js.map